import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Workforce, WORKFORCE_STATUSES } from "@/types/workforce";
import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuSub,
	DropdownMenuSubContent,
	DropdownMenuSubTrigger,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MoreVertical, Circle, CalendarPlus, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ExtendWorkforceModal } from "./ExtendWorkforceModal";
import { BackButton } from "@/components/ui/BackButton";
import { FavoriteButton } from "@/components/favorites/FavoriteButton";
import { NotificationSubscriptionButton } from "@/components/notifications/NotificationSubscriptionButton";

interface WorkforceDetailHeaderProps {
	workforce: Workforce;
	isEditing: boolean;
	onEdit: () => void;
	onDelete: () => void;
	onStatusChange: (status: string) => void;
	onRefresh: () => void;
}

export const WorkforceDetailHeader = ({
	workforce,
	isEditing,
	onEdit,
	onDelete,
	onStatusChange,
	onRefresh,
}: WorkforceDetailHeaderProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);

	const candidateName = workforce.candidate?.name || 'Assignment';

	return (
		<>
			<div className="flex justify-between items-center mb-6">
				<BackButton
					to="/workforce"
					label={t("common.back", "Back")}
				/>
				<div className="flex gap-2">
					{/* Favorite Button */}
					<FavoriteButton
						entityType="workforce"
						entityId={workforce.id}
						entityName={candidateName}
						variant="outline"
						size="icon"
					/>

					{/* Notification Button */}
					<NotificationSubscriptionButton
						entityType="workforce"
						entityId={workforce.id}
						entityName={candidateName}
						variant="outline"
						size="icon"
					/>

					{/* Edit Button */}
					{!isEditing && (
						<Button
							variant="outline"
							size="icon"
							onClick={onEdit}
							title={t("common.edit", "Edit")}
						>
							<Pencil className="h-4 w-4" />
						</Button>
					)}

					{/* More Actions Dropdown */}
					{!isEditing && (
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									variant="outline"
									size="icon"
									title={t("common.moreActions", "More actions")}
								>
									<MoreVertical className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{/* Extend - only for active assignments */}
								{workforce.status === 'active' && (
									<DropdownMenuItem
										onClick={() => setIsExtendModalOpen(true)}
										className="gap-2"
									>
										<CalendarPlus className="h-4 w-4" />
										{t("workforce.actions.extend", "Extend Assignment")}
									</DropdownMenuItem>
								)}

								{/* Change Status submenu */}
								<DropdownMenuSub>
									<DropdownMenuSubTrigger className="gap-2">
										<Circle className="h-4 w-4" />
										{t("workforce.actions.changeStatus", "Change Status")}
									</DropdownMenuSubTrigger>
									<DropdownMenuSubContent>
										{WORKFORCE_STATUSES.map((status) => (
											<DropdownMenuItem
												key={status.value}
												onClick={() => onStatusChange(status.value)}
												disabled={workforce.status === status.value}
												className="gap-2"
											>
												<Circle className="h-2 w-2 fill-current" />
												{status.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuSubContent>
								</DropdownMenuSub>

								<DropdownMenuSeparator />

								<DropdownMenuItem
									onClick={() => workforce.candidate && navigate(`/candidates/${workforce.candidate.id}`)}
								>
									{t("workforce.actions.viewCandidate", "View Consultant Profile")}
								</DropdownMenuItem>
								{workforce.job && (
									<DropdownMenuItem
										onClick={() => navigate(`/jobs/${workforce.job!.id}`)}
									>
										{t("workforce.actions.viewJob", "View Job")}
									</DropdownMenuItem>
								)}
							</DropdownMenuContent>
						</DropdownMenu>
					)}

				</div>
			</div>

			{/* Extend Modal */}
			<ExtendWorkforceModal
				workforce={workforce}
				open={isExtendModalOpen}
				onOpenChange={setIsExtendModalOpen}
				onSuccess={onRefresh}
			/>

		</>
	);
};
